import { useCallback, useEffect, useRef, useState } from 'react';
import type { HouseAd } from '../ads/houseAds';
import { preloadImage } from '../lib/preload';
import type { AdSchedule } from '../services/api';
import { useAds } from './useAds';

// Free tier: after the scheduled minutes of *visible* viewing time (default 60), show one
// house ad for the scheduled seconds (default 30), then resume. The schedule and order
// are set at /admin. Viewing time and the position in the sequence persist across
// reloads, so refreshing doesn't skip the ad. Licensed users never mount the timer.
// The ad's photo is downloaded before it appears; one that doesn't arrive in time is shown
// on its colour scene instead of on black.

const CLOCK_KEY = 'kcl-canvas-ad-clock';
const LAST_KEY = 'kcl-canvas-ad-last';
const TICK_MS = 5000;
/** Longest an ad waits for its photo before appearing without it. */
const AD_IMAGE_WAIT_MS = 20_000;

function readNumber(key: string, fallback: number): number {
  try {
    const v = Number(localStorage.getItem(key));
    return localStorage.getItem(key) !== null && Number.isFinite(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

function writeNumber(key: string, n: number): void {
  try {
    localStorage.setItem(key, String(n));
  } catch {
    // storage blocked – the in-memory values still work
  }
}

/** Picks the next ad: the following one in admin order, or a random one other than the last. */
export function nextAdIndex(order: AdSchedule['order'], count: number, last: number, random = Math.random): number {
  if (count <= 0) return -1;
  if (order === 'sequence') return last < 0 ? 0 : (last + 1) % count;
  if (count === 1) return 0;
  const pick = Math.floor(random() * (count - 1));
  return last >= 0 && last < count && pick >= last ? pick + 1 : pick;
}

export function useAdManager(enabled: boolean) {
  const [ad, setAd] = useState<HouseAd | null>(null);
  const loading = useRef(false);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const elapsed = useRef(readNumber(CLOCK_KEY, 0));
  const { ads, schedule } = useAds(enabled);
  const latest = useRef({ ads, schedule });
  latest.current = { ads, schedule };
  const last = useRef(readNumber(LAST_KEY, -1));

  const dismiss = useCallback(() => setAd(null), []);

  useEffect(() => {
    if (!enabled) {
      setAd(null);
      return;
    }
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || ad || loading.current) return;
      elapsed.current += TICK_MS;
      const { ads: list, schedule: s } = latest.current;
      if (elapsed.current >= s.intervalMin * 60_000) {
        elapsed.current = 0;
        last.current = nextAdIndex(s.order, list.length, last.current);
        writeNumber(LAST_KEY, last.current);
        const pick = list[last.current];
        if (pick?.image) {
          loading.current = true;
          void preloadImage(pick.image, AD_IMAGE_WAIT_MS).then((ok) => {
            loading.current = false;
            // Licence bought (or ads switched off) while the photo was loading: no ad.
            if (enabledRef.current) setAd(ok ? pick : { ...pick, image: null });
          });
        } else {
          setAd(pick ?? null);
        }
      }
      writeNumber(CLOCK_KEY, elapsed.current);
    }, TICK_MS);
    return () => window.clearInterval(timer);
  }, [enabled, ad]);

  const durationMs = schedule.durationSec * 1000;
  useEffect(() => {
    if (!ad) return;
    const t = window.setTimeout(dismiss, durationMs);
    return () => window.clearTimeout(t);
  }, [ad, dismiss, durationMs]);

  return { ad, dismiss };
}
